import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { useIntl } from '@edx/frontend-platform/i18n';

import CourseCompletionCheckMark from '../../views/CourseWorking/CourseCompletionCheckMark';
import { getFirstAvailableUnit, getParameterByName } from '../../utils';
import type { CourseOutline } from '../../types';

import './index.scss';
import messages from './messages';

interface CourseMapProps {
  courseOutlineData: any;
  unitID: string | undefined;
  courseID: string | undefined;
  handleRefreshOutline: () => void;
}

const CourseSideItemContext = React.createContext<CourseSideItemContextType | null>(null);
const CourseSideItemProvider = CourseSideItemContext.Provider;

interface CourseSideItemContextType {
  handleOutlineClick: (parentId: string, unitId: string) => void;
  handleRefreshOutline: () => void;
  setHighlightOnMenu: () => void;
  activeCourseID: string;
  activeSecondLevelOutline: string | null;
  activeFirstLevelOutline: string | null;
  setActiveCourseID: React.Dispatch<React.SetStateAction<string>>;
  setActiveSecondLevelOutline: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveFirstLevelOutline: React.Dispatch<React.SetStateAction<string | null>>;
}

const SecondLevelOutline: React.FC<{ data: CourseOutline; subsectionId: string }> = ({ data, subsectionId }) => {
  const intl = useIntl();
  const context = useContext(CourseSideItemContext);

  if (!context) {
    throw new Error(intl.formatMessage(messages.contextError));
  }

  const { handleOutlineClick, activeCourseID, handleRefreshOutline } = context;

  const handleClick = () => {
    handleOutlineClick(subsectionId, data.id);
    handleRefreshOutline();
  };

  return (
    <div className="third-level-outline faq-content-3 open">
      <div className="inner open">
        <div // eslint-disable-line
          data-id={data.id}
          className={`module_row ${activeCourseID === data.id ? 'active' : ''}`}
          onClick={handleClick}
        >
          <div className="div-block-632 w-clearfix">
            <div className="inner_text">
              {data?.complete ? (
                // @ts-ignore
                <IoCheckmarkCircle color="#0A3887" size={17} style={{ minWidth: '17px' }} />
              ) : (
                <CourseCompletionCheckMark courseData={data} />
              )}
              <span className="c_l">
                {data.display_name}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CourseOutlineItem: React.FC<{ item: CourseOutline }> = ({ item }) => (
  <div className="faq-question-wrap-3" key={`course-outline-child-${item.block_id}`}>
    {Array.isArray(item.children) && item.children.map((childItem: CourseOutline) => (
      Array.isArray(childItem.children) && childItem.children.length > 0 && (
        <SecondLevelOutline
          key={`course-outline-child-${childItem.block_id}`}
          data={childItem}
          subsectionId={item.id}
        />
      )))}
  </div>
);

const CourseMap: React.FC<CourseMapProps> = ({
  courseID,
  courseOutlineData,
  unitID,
  handleRefreshOutline,
}) => {
  const [activeCourseID, setActiveCourseID] = useState<string>(unitID || '');
  const [activeSecondLevelOutline, setActiveSecondLevelOutline] = useState<string | null>(null);
  const [activeFirstLevelOutline, setActiveFirstLevelOutline] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleOutlineClick = (subsectionId: string, selectedUnitID: string) => {
    navigate(`/course-working/${courseID}/${subsectionId}/${selectedUnitID}`);
  };

  const setHighlightOnMenu = () => {
    const unitId = (
      getParameterByName('unit_id') || unitID || getFirstAvailableUnit(courseOutlineData)
    );
    setActiveCourseID(unitId);
  };

  useEffect(() => {
    setHighlightOnMenu();
  }, [courseOutlineData, courseID, unitID]);

  return (
    <CourseSideItemProvider value={{
      handleOutlineClick,
      setHighlightOnMenu,
      activeCourseID,
      activeSecondLevelOutline,
      activeFirstLevelOutline,
      setActiveCourseID,
      setActiveSecondLevelOutline,
      setActiveFirstLevelOutline,
      handleRefreshOutline,
    }}
    >
      {courseOutlineData?.children?.map((item: CourseOutline) => (
        <CourseOutlineItem
          key={`course-outline-${item.block_id}`}
          item={item}
        />
      ))}
    </CourseSideItemProvider>
  );
};

export default CourseMap;
